import type { ReactElement } from 'react';

import { SCREEN_PATHS } from '@/shared/routes';

/* One icon per navigation entry.
 *
 * **Hand-written SVG paths rather than an icon package**, and that is hard rule 13 rather than a
 * preference: a webfont icon set fetches from a CDN, and the packages that ship the paths locally
 * (`lucide-react`, `react-icons`) add a dependency whose licence and air-gap story would need
 * checking for thirty glyphs. These are stroke paths in the file, so the bundle carries exactly the
 * thirty it uses and nothing resolves over a network.
 *
 * **Keyed on the route, not on the label key.** A route is the thing that cannot be duplicated —
 * `navigation.test.ts` already asserts every entry resolves to one — so a missing icon is a missing
 * route, which the existing test catches. Keying on the label would let two entries share a glyph
 * silently.
 *
 * **A missing entry renders nothing rather than a placeholder.** The row still lays out, because the
 * icon slot is a fixed-width flex item; a fallback glyph would put the *same* mark beside two
 * different destinations, which is worse than an absence in a list a reader scans by shape.
 *
 * The glyphs are chosen to be distinguishable at 15px from their *silhouette*, not their detail —
 * a database cylinder against a table grid against a branch, rather than three variations on a
 * document. That is what makes an icon column worth its width: the reader stops reading the labels.
 */

/* 15px, 1.8px stroke, round caps, and 0.8 opacity — the reference's values. A heavier stroke at a
 * smaller size is the right trade: at 15px a 1.5px line reads as grey mush, and the opacity is what
 * keeps the column of glyphs a texture rather than a row of marks competing with the words.
 *
 * `currentColor` means the icon inherits the row's ink, so the active and hover states move the glyph
 * with the label rather than needing rules of their own. */
/* A plain function, deliberately **not** a component.
 *
 * `Glyph` as a capitalised component tripped `react-refresh/only-export-components`: a module that
 * exports a component *and* a non-component breaks fast refresh, and this file's whole purpose is to
 * export `navIcon`. Lowercasing it makes the wrapper a helper that returns an element — the same
 * output, no component boundary, and no per-icon `<Glyph>` element in the tree for React to
 * reconcile. */
function glyph(children: ReactElement): ReactElement {
  return (
    <svg
      className="lq-nav__icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      /* Decorative: the label beside it is the accessible name, and announcing both would make a
         screen reader read every entry twice. */
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

const ICONS: Readonly<Record<string, ReactElement>> = {
  /* Home — a house. */
  [SCREEN_PATHS.home]: glyph(
    <>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20h14V9.5" />
      <path d="M9.5 20v-5.5h5V20" />
    </>,
  ),
  /* Connections — a database cylinder. */
  [SCREEN_PATHS.connections]: glyph(
    <>
      <ellipse cx="12" cy="5.5" rx="7.5" ry="2.8" />
      <path d="M4.5 5.5v6c0 1.55 3.36 2.8 7.5 2.8s7.5-1.25 7.5-2.8v-6" />
      <path d="M4.5 11.5v6c0 1.55 3.36 2.8 7.5 2.8s7.5-1.25 7.5-2.8v-6" />
    </>,
  ),
  /* Datasets — a table: a header band and a column split. */
  [SCREEN_PATHS.datasets]: glyph(
    <>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
      <path d="M3.5 9.5h17" />
      <path d="M10 9.5v10" />
    </>,
  ),
  /* Lineage — a branch. */
  [SCREEN_PATHS.lineage]: glyph(
    <>
      <circle cx="7" cy="5.5" r="2" />
      <circle cx="7" cy="18.5" r="2" />
      <circle cx="17" cy="9" r="2" />
      <path d="M7 7.5v9" />
      <path d="M17 11v1.5a4 4 0 0 1-4 4H9" />
    </>,
  ),
  /* PII review — a shield. */
  [SCREEN_PATHS.review]: glyph(
    <path d="M12 3.5 5 6v6c0 4.2 2.9 7.4 7 8.5 4.1-1.1 7-4.3 7-8.5V6l-7-2.5Z" />,
  ),
  /* Rules — a checked box. */
  [SCREEN_PATHS.rules]: glyph(
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="2.5" />
      <path d="M8 12.2l2.8 2.8L16.5 9.3" />
    </>,
  ),
  /* Executions — a play triangle. */
  [SCREEN_PATHS.executions]: glyph(<path d="M7 4.8v14.4L19.5 12 7 4.8Z" />),
  /* Scores — a gauge. */
  [SCREEN_PATHS.scores]: glyph(
    <>
      <path d="M3.5 16a8.5 8.5 0 1 1 17 0" />
      <path d="M12 16l4.2-4.6" />
    </>,
  ),
  /* Anomalies — a heartbeat line. */
  [SCREEN_PATHS.anomalies]: glyph(<path d="M2.5 12h4l2.5-6 4 12 2.5-6h6" />),
  /* Incidents — a warning triangle. */
  [SCREEN_PATHS.incidents]: glyph(
    <>
      <path d="M12 3.8 2.8 19.5h18.4L12 3.8Z" />
      <path d="M12 9.5v4" />
      <path d="M12 16.6h.01" />
    </>,
  ),
  /* Alert routing — a bell. */
  [SCREEN_PATHS.alertRouting]: glyph(
    <>
      <path d="M6.5 10a5.5 5.5 0 0 1 11 0c0 4 1.5 5.5 1.5 5.5H5S6.5 14 6.5 10Z" />
      <path d="M10 18.5a2.2 2.2 0 0 0 4 0" />
    </>,
  ),
  /* Reconciliation — two arrows, opposed. */
  [SCREEN_PATHS.reconciliation]: glyph(
    <>
      <path d="M3.5 8h14" />
      <path d="M14.5 5 17.5 8l-3 3" />
      <path d="M20.5 16h-14" />
      <path d="M9.5 13 6.5 16l3 3" />
    </>,
  ),
  /* Access policy — a padlock. */
  [SCREEN_PATHS.policy]: glyph(
    <>
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" />
      <path d="M8 10.5V7.8a4 4 0 0 1 8 0v2.7" />
    </>,
  ),
  /* Compliance — a clipboard with a tick. */
  [SCREEN_PATHS.compliance]: glyph(
    <>
      <rect x="4.5" y="4.5" width="15" height="16" rx="2.5" />
      <path d="M9 4.5V3.2h6v1.3" />
      <path d="M8.5 12.6l2.6 2.6 4.4-4.6" />
    </>,
  ),
  /* Contracts — a document with a folded corner. */
  [SCREEN_PATHS.contracts]: glyph(
    <>
      <path d="M6 3.5h7.5L19 9v11.5H6V3.5Z" />
      <path d="M13.5 3.5V9H19" />
    </>,
  ),
  /* Remediation — a spanner. */
  [SCREEN_PATHS.remediation]: glyph(
    <path d="M15.6 3.6a5 5 0 0 0-1.2 8.1L4.5 21.5l-2-2L12.3 9.6A5 5 0 0 0 20.4 8.4l-3.2-1.6-1.6-3.2Z" />,
  ),
  /* Bulk changes — stacked layers. */
  [SCREEN_PATHS.bulk]: glyph(
    <>
      <path d="M12 3.5 3 8l9 4.5L21 8l-9-4.5Z" />
      <path d="M3 12.5 12 17l9-4.5" />
      <path d="M3 16.5 12 21l9-4.5" />
    </>,
  ),
  /* Autonomous — sliders, as in a control it runs itself under. */
  [SCREEN_PATHS.autonomous]: glyph(
    <>
      <path d="M3.5 7.5h11" />
      <path d="M18.5 7.5h2" />
      <circle cx="16.5" cy="7.5" r="2" />
      <path d="M3.5 16.5h2" />
      <path d="M9.5 16.5h11" />
      <circle cx="7.5" cy="16.5" r="2" />
    </>,
  ),
  /* Copilot — a speech bubble. */
  [SCREEN_PATHS.copilot]: glyph(
    <path d="M20.5 12c0 4.1-3.8 7.4-8.5 7.4a9.8 9.8 0 0 1-2.7-.4L4.5 20.5l1.3-3.6A7 7 0 0 1 3.5 12c0-4.1 3.8-7.4 8.5-7.4s8.5 3.3 8.5 7.4Z" />,
  ),
  /* Suggestions — a lightbulb. */
  [SCREEN_PATHS.suggestions]: glyph(
    <>
      <path d="M9 16.2a6 6 0 1 1 6 0v1.3H9v-1.3Z" />
      <path d="M9.8 20.5h4.4" />
    </>,
  ),
  /* Model checks — a circled tick. */
  [SCREEN_PATHS.evals]: glyph(
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M8.2 12.2l2.8 2.8 4.8-5.2" />
    </>,
  ),
  /* Model routing — crossing arrows. */
  [SCREEN_PATHS.serving]: glyph(
    <>
      <path d="M3.5 5.5h3.4l10.6 13h3" />
      <path d="M18 15.5l2.5 3-2.5 3" />
      <path d="M3.5 18.5h3.4l3.6-4.4" />
      <path d="M14.4 9.5l2.5-4h3.6" />
      <path d="M18 2.5l2.5 3-2.5 3" />
    </>,
  ),
  /* Marketplace — a package. */
  [SCREEN_PATHS.marketplace]: glyph(
    <>
      <path d="M20.5 8.2v7.6L12 20.5 3.5 15.8V8.2L12 3.5l8.5 4.7Z" />
      <path d="M3.5 8.2 12 12.9l8.5-4.7" />
      <path d="M12 12.9v7.6" />
    </>,
  ),
  /* Audit — a list, as in an ordered record. */
  [SCREEN_PATHS.audit]: glyph(
    <>
      <path d="M9 6.5h11.5" />
      <path d="M9 12h11.5" />
      <path d="M9 17.5h11.5" />
      <path d="M4.5 6.5h.01" />
      <path d="M4.5 12h.01" />
      <path d="M4.5 17.5h.01" />
    </>,
  ),
  /* Users & access — two people. */
  [SCREEN_PATHS.admin]: glyph(
    <>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3.2 20.5c0-3.2 2.6-5.4 5.8-5.4s5.8 2.2 5.8 5.4" />
      <path d="M16 5.2a3.2 3.2 0 0 1 0 6.1" />
      <path d="M17.4 15.4c2.1.5 3.4 2.2 3.4 4.4" />
    </>,
  ),
};

export function navIcon(to: string): ReactElement | null {
  return ICONS[to] ?? null;
}
